// The story board editor's five-step walkthrough (#1578 item 10). Shown once
// per person, the first time they open the editor; the help icon in the bar
// reopens it any time. It points at the bar controls that carry the editor's
// core ideas — editing text, locked figures, the agent, save vs publish, and
// versions — rather than at anything inside the iframe, which is a separate
// document a host Popper cannot anchor into reliably.
//
// Persisted per browser (localStorage via usePersistedFlag), not per server
// account: Adam, 28 Sep — a small user_flags table can replace this later if
// Matt wants explainers on every page, but this one page does not need it yet.
import React, { useEffect, useState } from 'react'
import { Box, Button, Fade, Paper, Popper, Typography } from '@mui/material'
import { CARD_RADIUS, HAIRLINE } from '../../agent'

interface Step {
  /** The bar element this step points at (a `data-testid`). */
  anchorTestId: string
  title: string
  body: string
  placement: 'bottom-start' | 'bottom' | 'bottom-end'
}

const STEPS: Step[] = [
  {
    anchorTestId: 'story-editor-title',
    title: 'Edit text',
    body: 'Click any heading or paragraph on the page and type. Changes save as a draft until you publish them.',
    placement: 'bottom-start',
  },
  {
    anchorTestId: 'story-editor-undo-redo',
    title: 'Locked numbers',
    body: 'Charts, tables and figures are locked — they come from the data. Hover one to see where from, and ask the agent to change it.',
    placement: 'bottom-start',
  },
  {
    anchorTestId: 'story-ask-agent',
    title: 'Ask the agent',
    body: 'Anything the page cannot do by hand — a new chart, different data, a different look — the agent can.',
    placement: 'bottom-end',
  },
  {
    anchorTestId: 'story-editor-save-button',
    title: 'Save vs publish',
    body: 'Save keeps a draft only you see. Publish is the separate step that updates the share link and any dashboards.',
    placement: 'bottom',
  },
  {
    anchorTestId: 'story-editor-versions-button',
    title: 'Versions',
    body: 'Every save keeps a version. Open Versions to see the history and restore an earlier one.',
    placement: 'bottom',
  },
]

export interface StoryEditorExplainerProps {
  open: boolean
  onClose: () => void
}

/** The persisted-flag key for one person: seen once, not shown again automatically. */
export function explainerSeenKey(email: string): string {
  return `story-editor-explainer-seen:${email}`
}

export default function StoryEditorExplainer({ open, onClose }: StoryEditorExplainerProps) {
  const [stepIdx, setStepIdx] = useState(0)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  // Reset to the first step every time it (re)opens.
  useEffect(() => {
    if (open) setStepIdx(0)
  }, [open])

  const step = STEPS[stepIdx]
  // The bar element a step anchors to can still be a frame or two behind the
  // render that flips `open` (a rerender elsewhere settling, a transition
  // finishing) — one querySelector right on open is not reliable. Retry on
  // requestAnimationFrame, bounded, until it appears; this also covers
  // reopening via the help icon and moving between steps.
  useEffect(() => {
    if (!open) {
      setAnchorEl(null)
      return
    }
    let cancelled = false
    let frame = 0
    let attempts = 0
    const MAX_ATTEMPTS = 120 // ~2s at 60fps
    const tryFind = () => {
      if (cancelled) return
      const el = document.querySelector(`[data-testid="${step.anchorTestId}"]`)
      if (el) {
        setAnchorEl(el)
        return
      }
      attempts += 1
      if (attempts >= MAX_ATTEMPTS) return
      frame = requestAnimationFrame(tryFind)
    }
    setAnchorEl(null)
    tryFind()
    return () => {
      cancelled = true
      if (frame) cancelAnimationFrame(frame)
    }
  }, [open, step.anchorTestId])

  if (!open || !anchorEl) return null

  const last = stepIdx === STEPS.length - 1

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement={step.placement}
      transition
      data-testid="story-editor-explainer"
      // A walkthrough with its own buttons, not a hover hint: Popper's default
      // role is "tooltip", which also reads as a stray tooltip to anything
      // waiting for tooltips to close.
      role="dialog"
      aria-label={`Editor tour, step ${stepIdx + 1} of ${STEPS.length}`}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={150}>
          <Paper
            elevation={0}
            sx={{ mt: 1, width: 300, p: 2, borderRadius: CARD_RADIUS, border: HAIRLINE, boxShadow: '0 8px 28px rgba(38,55,87,0.16)' }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{step.title}</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>{step.body}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{stepIdx + 1} of {STEPS.length}</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!last && (
                  <Button size="small" color="info" onClick={onClose} data-testid="story-editor-explainer-skip" sx={{ textTransform: 'none' }}>
                    Skip
                  </Button>
                )}
                {stepIdx > 0 && (
                  <Button size="small" color="info" onClick={() => setStepIdx((n) => n - 1)} sx={{ textTransform: 'none' }}>
                    Back
                  </Button>
                )}
                <Button
                  size="small"
                  color="info"
                  variant="contained"
                  disableElevation
                  onClick={() => (last ? onClose() : setStepIdx((n) => n + 1))}
                  data-testid="story-editor-explainer-next"
                  sx={{ textTransform: 'none' }}
                >
                  {last ? 'Done' : 'Next'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  )
}
